import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { formatApiError } from "@/lib/api";
import { loadDemoSettings, clearDemoSettings } from "@/lib/demo";
import { GOALS, goalKey, primaryGoal, needsPace, DEMO_GOAL, DEMO_DIET, DEMO_DAY } from "@/lib/goals";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Leaf, ArrowLeft, ArrowRight, Check } from "lucide-react";

function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all ${
        active ? "border-primary bg-primary text-primary-foreground shadow-sm"
               : "border-border bg-card hover:border-chenia-terracotta/50 text-foreground/80"}`}>
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2.5">
      <Label className="text-base font-medium">{label}</Label>
      {children}
    </div>
  );
}

export default function Onboarding() {
  const { t } = useLang();
  const { user, setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [demo] = useState(loadDemoSettings);
  const answered = demo.answered || [];
  const demoGoals = (demo.goals || []).map((g) => DEMO_GOAL[g]).filter(Boolean);
  const [showAll, setShowAll] = useState(false);
  const [pos, setPos] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [d, setD] = useState({
    first_name: user?.first_name || "", age: demo.age ? String(demo.age) : "", sex: demo.sex || "",
    height_cm: "", weight_kg: "", activity_level: "",
    goals: answered.includes("goals") ? demoGoals : [], pace: "moderate",
    diet: DEMO_DIET[demo.diet] || "omnivore", allergies: demo.allergies || [], health_contexts: [],
    meals_per_day: [3, 4, 5].includes(demo.mealsPerDay) ? demo.mealsPerDay : 3, household_size: demo.persons || 1,
    cooking_time: "standard", cooking_level: "beginner", weekly_budget: "",
    excluded_foods: "", liked_foods: "", disliked_foods: "", equipment: [],
  });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setD((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const activities = ["sedentary", "light", "moderate", "active", "very_active"];
  const paces = ["pace_slow", "pace_moderate", "pace_fast"];
  const paceVal = { pace_slow: "slow", pace_moderate: "moderate", pace_fast: "fast" };
  const diets = ["omnivore", "vegetarian", "vegan", "pescatarian", "mediterranean", "anti_inflammatory"];
  const healthContexts = ["breastfeeding", "pcos", "endometriosis", "adenomyosis", "insulin_resistance", "diabetes"];
  const allergensList = ["gluten", "dairy", "nuts", "eggs", "fish", "shellfish", "soy"];
  const times = ["ck_quick", "ck_standard", "ck_generous"];
  const timeVal = { ck_quick: "quick", ck_standard: "standard", ck_generous: "generous" };
  const levels = ["beginner", "intermediate", "advanced"];
  const equip = ["oven", "stove", "microwave", "blender", "airfryer"];

  const steps = [
    { key: "s_profile", valid: () => d.first_name && d.age && d.sex },
    { key: "s_body", valid: () => d.height_cm && d.weight_kg && d.activity_level },
    { key: "s_goal", valid: () => d.goals.length > 0 && d.pace },
    { key: "s_prefs", valid: () => d.diet && d.meals_per_day },
    { key: "s_practical", valid: () => true },
  ];
  // Étapes déjà renseignées pendant la semaine d'essai : non redemandées (modifiables via « Vérifier ou modifier »)
  const prefilled = new Set();
  if (answered.includes("age") && answered.includes("sex") && demo.age && demo.sex && user?.first_name) prefilled.add(0);
  if (answered.includes("goals") && demoGoals.length) prefilled.add(2);
  if (answered.includes("allergens") && answered.includes("diet") && answered.includes("meals")) prefilled.add(3);
  const flow = steps.map((_, i) => i).filter((i) => showAll || !prefilled.has(i));
  const step = flow[Math.min(pos, flow.length - 1)];
  const isLast = pos >= flow.length - 1;
  const pct = Math.round(((pos + 1) / flow.length) * 100);

  const finish = async () => {
    setBusy(true); setErr("");
    const payload = {
      first_name: d.first_name, age: Number(d.age), sex: d.sex,
      height_cm: Number(d.height_cm), weight_kg: Number(d.weight_kg),
      activity_level: d.activity_level, goals: d.goals, goal: primaryGoal(d.goals), pace: d.pace,
      diet: d.diet, allergies: d.allergies, health_contexts: d.health_contexts,
      meals_per_day: Number(d.meals_per_day), household_size: Number(d.household_size),
      cooking_time: d.cooking_time, cooking_level: d.cooking_level,
      weekly_budget: d.weekly_budget ? Number(d.weekly_budget) : null,
      excluded_foods: d.excluded_foods.split(",").map((s) => s.trim()).filter(Boolean),
      liked_foods: d.liked_foods.split(",").map((s) => s.trim()).filter(Boolean),
      disliked_foods: d.disliked_foods.split(",").map((s) => s.trim()).filter(Boolean),
      equipment: d.equipment,
    };
    try {
      await api.post("/onboarding/complete", payload);
      const presentDays = (demo.days || []).map((x) => DEMO_DAY[x]).filter(Boolean);
      if (presentDays.length && presentDays.length < 7) await api.put("/household/members/primary", { present_days: presentDays }).catch(() => {});
      clearDemoSettings();
      await refresh();
      const requestedPlan = new URLSearchParams(window.location.search).get("plan");
      const plan = requestedPlan === "plus" || requestedPlan === "premium" ? requestedPlan : null;
      nav(plan ? `/pricing?plan=${plan}` : "/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const next = () => { if (!isLast) setPos(pos + 1); else finish(); };

  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto max-w-3xl px-4 sm:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span>
          <span className="font-serif text-2xl font-semibold tracking-tight">CHENIA</span>
        </div>
        <LanguageSelector />
      </header>

      <div className="mx-auto max-w-2xl px-4 sm:px-8 pb-16">
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
            <span>{t("onb_step")} {pos + 1} {t("onb_of")} {flow.length}</span>
            <span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" data-testid="onboarding-progress" />
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mb-1">{t("onb_title")}</h1>
        <p className="text-muted-foreground mb-8">{t(steps[step].key)}</p>
        {prefilled.size > 0 && !showAll && (
          <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-chenia-sage/40 bg-chenia-sage/15 p-4 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="onb-demo-banner">
            <p className="leading-relaxed">{t("onb_demo_banner")}</p>
            <Button type="button" variant="outline" size="sm" className="rounded-full shrink-0" data-testid="onb-demo-edit" onClick={() => { setShowAll(true); setPos(0); }}>{t("onb_demo_edit")}</Button>
          </div>
        )}

        <div className="chenia-card p-6 sm:p-8 space-y-7 chenia-fade-up" key={step} data-testid={`onboarding-step-${step}`}>
          {step === 0 && (<>
            <Field label={t("first_name")}>
              <Input data-testid="onb-firstname" value={d.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-11 rounded-xl" />
            </Field>
            <Field label={t("age")}>
              <Input data-testid="onb-age" type="number" value={d.age} onChange={(e) => set("age", e.target.value)} className="h-11 rounded-xl" />
            </Field>
            <Field label={t("sex")}>
              <div className="flex flex-wrap gap-2.5">
                {["male", "female", "other"].map((s) => (
                  <Pill key={s} testid={`onb-sex-${s}`} active={d.sex === s} onClick={() => set("sex", s)}>{t(s)}</Pill>
                ))}
              </div>
            </Field>
          </>)}

          {step === 1 && (<>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("height")}>
                <Input data-testid="onb-height" type="number" value={d.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="h-11 rounded-xl" />
              </Field>
              <Field label={t("weight")}>
                <Input data-testid="onb-weight" type="number" value={d.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="h-11 rounded-xl" />
              </Field>
            </div>
            <Field label={t("activity")}>
              <div className="flex flex-wrap gap-2.5">
                {activities.map((a) => (
                  <Pill key={a} testid={`onb-activity-${a}`} active={d.activity_level === a} onClick={() => set("activity_level", a)}>{t(a)}</Pill>
                ))}
              </div>
            </Field>
          </>)}

          {step === 2 && (<>
            <Field label={t("goals_multi")}>
              <p className="-mt-1 mb-3 text-xs text-muted-foreground">{t("goals_hint")}</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {GOALS.map((g) => (
                  <Pill key={g} testid={`onb-goal-${g}`} active={d.goals.includes(g)} onClick={() => toggle("goals", g)}>{t(goalKey(g))}</Pill>
                ))}
              </div>
            </Field>
            {needsPace(d.goals) && <Field label={t("pace")}>
              <div className="flex flex-wrap gap-2.5">
                {paces.map((p) => (
                  <Pill key={p} testid={`onb-pace-${paceVal[p]}`} active={d.pace === paceVal[p]} onClick={() => set("pace", paceVal[p])}>{t(p)}</Pill>
                ))}
              </div>
            </Field>}
          </>)}

          {step === 3 && (<>
            <Field label={t("diet")}>
              <div className="flex flex-wrap gap-2.5">
                {diets.map((x) => (
                  <Pill key={x} testid={`onb-diet-${x}`} active={d.diet === x} onClick={() => set("diet", x)}>{t(x)}</Pill>
                ))}
              </div>
            </Field>
            <Field label={t("allergies")}>
              <div className="flex flex-wrap gap-2.5">
                {allergensList.map((a) => (
                  <Pill key={a} testid={`onb-allergy-${a}`} active={d.allergies.includes(a)} onClick={() => toggle("allergies", a)}>{t(`al_${a}`)}</Pill>
                ))}
              </div>
            </Field>
            <Field label={t("health_contexts")}>
              <div className="flex flex-wrap gap-2.5">
                {healthContexts.map((context) => (
                  <Pill key={context} testid={"onb-health-" + context} active={d.health_contexts.includes(context)} onClick={() => toggle("health_contexts", context)}>{t(context)}</Pill>
                ))}
              </div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t("health_context_note")}</p>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("meals_per_day")}>
                <div className="flex gap-2.5">
                  {[3, 4, 5].map((n) => (
                    <Pill key={n} testid={`onb-meals-${n}`} active={d.meals_per_day === n} onClick={() => set("meals_per_day", n)}>{n}</Pill>
                  ))}
                </div>
              </Field>
              <Field label={t("household")}>
                <Input data-testid="onb-household" type="number" min={1} value={d.household_size} onChange={(e) => set("household_size", e.target.value)} className="h-11 rounded-xl" />
              </Field>
            </div>
          </>)}

          {step === 4 && (<>
            <Field label={t("cooking_time")}>
              <div className="flex flex-wrap gap-2.5">
                {times.map((x) => (
                  <Pill key={x} testid={`onb-time-${timeVal[x]}`} active={d.cooking_time === timeVal[x]} onClick={() => set("cooking_time", timeVal[x])}>{t(x)}</Pill>
                ))}
              </div>
            </Field>
            <Field label={t("cooking_level")}>
              <div className="flex flex-wrap gap-2.5">
                {levels.map((x) => (
                  <Pill key={x} testid={`onb-level-${x}`} active={d.cooking_level === x} onClick={() => set("cooking_level", x)}>{t(`lvl_${x}`)}</Pill>
                ))}
              </div>
            </Field>
            <Field label={t("equipment")}>
              <div className="flex flex-wrap gap-2.5">
                {equip.map((x) => (
                  <Pill key={x} testid={`onb-equip-${x}`} active={d.equipment.includes(x)} onClick={() => toggle("equipment", x)}>{t(`eq_${x}`)}</Pill>
                ))}
              </div>
            </Field>
            <Field label={t("budget")}>
              <Input data-testid="onb-budget" type="number" value={d.weekly_budget} onChange={(e) => set("weekly_budget", e.target.value)} className="h-11 rounded-xl" />
            </Field>
            <Field label={t("excluded")}>
              <Input data-testid="onb-excluded" value={d.excluded_foods} onChange={(e) => set("excluded_foods", e.target.value)} placeholder={t("excluded_ph")} className="h-11 rounded-xl" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("liked")}>
                <Input data-testid="onb-liked" value={d.liked_foods} onChange={(e) => set("liked_foods", e.target.value)} placeholder={t("liked_ph")} className="h-11 rounded-xl" />
              </Field>
              <Field label={t("disliked")}>
                <Input data-testid="onb-disliked" value={d.disliked_foods} onChange={(e) => set("disliked_foods", e.target.value)} placeholder={t("disliked_ph")} className="h-11 rounded-xl" />
              </Field>
            </div>
          </>)}

          {err && <p className="text-sm text-destructive" data-testid="onboarding-error">{err}</p>}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" data-testid="onboarding-back" disabled={pos === 0 || busy}
            onClick={() => setPos(Math.max(0, pos - 1))} className="rounded-full">
            <ArrowLeft className="mr-1 h-4 w-4" /> {t("back")}
          </Button>
          <Button data-testid="onboarding-next" disabled={!steps[step].valid() || busy}
            onClick={next} className="rounded-full px-6 bg-primary hover:bg-chenia-terracottadark">
            {isLast
              ? (busy ? t("saving") : (<>{t("finish")} <Check className="ml-1 h-4 w-4" /></>))
              : (<>{t("next")} <ArrowRight className="ml-1 h-4 w-4" /></>)}
          </Button>
        </div>

        <p className="mt-6 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
      </div>
    </div>
  );
}
